import { useEffect } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { Box, Breadcrumbs, Card, CardContent, Link, Table, TableBody, TableHead } from '@mui/material';
import _ from 'lodash';

import { usePrevious } from 'common/hooks';
import { breadcrumbIconSx, breadcrumbLinkSx, breadcrumbsContainerSx, breadcrumbsSx } from 'common/styles';

import { DetailsCardTitle } from 'commonComponents/DetailsCardTitle';
import { LoadingIndicator } from 'commonComponents/LoadingIndicator';
import { PageContainer } from 'commonComponents/PageContainer';

import { useLicense } from 'api/licenses';
import { useCurrentProduct } from 'api/products';
import { UndoIcon } from 'icons';
import {
    useLicenseCustomerInfoRowDataMap,
    useLicenseInfoRowDataMap,
    useLicenseOrderInfoRowDataMap,
    useLicenseStatusInfoRowDataMap,
    useLicenseTermsRowDataMap,
} from 'licenses/licenseDetails';
import { licenseDetailBackContext } from 'licenses/LicenseDetails/LicenseDetailsContext';
import { LicenseFiles } from 'licenses/LicenseFiles/LicenseFiles';
import { ROLE_DEFINITIONS } from 'roles/definitions';
import { RoleDependentComponent } from 'roles/RoleDependentComponent';
import { ROUTES } from 'routes/routes';

import { LicenseCardActions } from './LicenseCardActions/LicenseCardActions';
import { LicenseDetailsHeaderRow, LicenseDetailsRow } from './LicenseDetailsRow';
import { LicenseHistory } from './LicenseHistory';

export function LicenseDetails() {
    const { id: licenseId } = useParams<{ id: string }>();
    const { data: currentProduct } = useCurrentProduct();
    const previousProduct = usePrevious(currentProduct);
    const { data: details, isLoading: isFetchingLicenseDetails } = useLicense(Number(licenseId));
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (previousProduct && !_.isEqual(previousProduct, currentProduct)) {
            navigate(ROUTES.LICENSE_LIST);
        }
    }, [currentProduct, previousProduct, navigate]);

    return (
        <PageContainer
            maxWidth="lg"
            data-testid="LicenseDetails"
            sx={{
                '& .MuiContainer-root': {
                    paddingTop: 0,
                },
            }}
        >
            <Box sx={breadcrumbsContainerSx}>
                <Breadcrumbs aria-label="breadcrumb" sx={breadcrumbsSx}>
                    {licenseDetailBackContext.map(context => (
                        <Link
                            key={context.url}
                            component={RouterLink}
                            to={context.url}
                            underline="hover"
                            data-testid="LicenseDetails_BreadcrumbLicenses"
                            sx={breadcrumbLinkSx}
                        >
                            <UndoIcon sx={breadcrumbIconSx} />
                            {context.label}
                        </Link>
                    ))}
                </Breadcrumbs>
            </Box>
            {isFetchingLicenseDetails || !details ? (
                <LoadingIndicator />
            ) : (
                <>
                    <Card>
                        <LicenseCardContent details={details} />
                        <LicenseCardActions licenseDetails={details} />
                    </Card>
                    <LicenseFiles license={details} initiallyOpen={location.state?.showLicenseFiles ?? false} />
                    <RoleDependentComponent
                        userRole={ROLE_DEFINITIONS.LicenseManager}
                        component={LicenseHistory}
                        licenseId={Number(licenseId)}
                    />
                </>
            )}
        </PageContainer>
    );
}

export function LicenseCardContent({ details }) {
    const showError = details.isExpired || details.internal || details.invalidated;
    const rowLicenseDataMap = useLicenseInfoRowDataMap();
    const rowLicenseTermsDataMap = useLicenseTermsRowDataMap();
    const rowLicenseCustomerInfoDataMap = useLicenseCustomerInfoRowDataMap();
    const rowLicenseOrderInfoDataMap = useLicenseOrderInfoRowDataMap();
    const rowLicenseStatusInfoDataMap = useLicenseStatusInfoRowDataMap();

    return (
        <CardContent data-testid="LicenseDetails_CardContent">
            <DetailsCardTitle
                subtitle={details.internal ? 'This license only for internal use' : ''}
                showError={showError}
                dataTestId="LicenseDetails"
            />
            <Table size="small" data-testid="LicenseDetails_LicenseInfoTable">
                <TableHead>
                    <LicenseDetailsHeaderRow title={'Product & License Identity'} />
                </TableHead>
                <TableBody>
                    {rowLicenseDataMap.map(rowConfig => (
                        <LicenseDetailsRow key={rowConfig.title} rowConfig={rowConfig} details={details} />
                    ))}
                </TableBody>
            </Table>
            <Table size="small" data-testid="LicenseDetails_LicenseTermsTable">
                <TableHead>
                    <LicenseDetailsHeaderRow title={'Entitlements & Terms'} />
                </TableHead>
                <TableBody>
                    {rowLicenseTermsDataMap.map(rowConfig => (
                        <LicenseDetailsRow key={rowConfig.title} rowConfig={rowConfig} details={details} />
                    ))}
                </TableBody>
            </Table>
            <Table size="small" data-testid="LicenseDetails_CustomerInfoTable">
                <TableHead>
                    <LicenseDetailsHeaderRow title={'Customer & Ownership'} />
                </TableHead>
                <TableBody>
                    {rowLicenseCustomerInfoDataMap.map(rowConfig => (
                        <LicenseDetailsRow key={rowConfig.title} rowConfig={rowConfig} details={details} />
                    ))}
                </TableBody>
            </Table>
            <Table size="small" data-testid="LicenseDetails_OrderInfoTable">
                <TableHead>
                    <LicenseDetailsHeaderRow title={'Order & Fulfillment'} />
                </TableHead>
                <TableBody>
                    {rowLicenseOrderInfoDataMap.map(rowConfig => (
                        <LicenseDetailsRow key={rowConfig.title} rowConfig={rowConfig} details={details} />
                    ))}
                </TableBody>
            </Table>
            <Table size="small" data-testid="LicenseDetails_OrderInfoTable">
                <TableHead>
                    <LicenseDetailsHeaderRow title={'Lifecycle & Status'} />
                </TableHead>
                <TableBody>
                    {rowLicenseStatusInfoDataMap.map(rowConfig => (
                        <LicenseDetailsRow key={rowConfig.title} rowConfig={rowConfig} details={details} />
                    ))}
                </TableBody>
            </Table>
        </CardContent>
    );
}
